import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

export default function DevisClient() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ signataire_nom: '', signataire_tel: '' });
  const [validating, setValidating] = useState(false);
  const [validated, setValidated] = useState(false);
  const [valMsg, setValMsg] = useState('');
  const [programmeDisponible, setProgrammeDisponible] = useState(false);
  const [showFichePopup, setShowFichePopup] = useState(false);

  useEffect(() => {
    if (validated) setShowFichePopup(true);
  }, [validated]);

  useEffect(() => {
    fetch(`${API_BASE}/devis-public/${token}`)
      .then(r => r.json())
      .then(d => {
        if (d.error) setError(d.error);
        else setData(d);
      })
      .catch(() => setError('Impossible de charger le devis'))
      .finally(() => setLoading(false));
  }, [token]);

  // Vérifier si le programme est disponible
  useEffect(() => {
    if (!token) return;
    // On fait un GET et on check le Content-Type pour savoir si c'est un PDF
    fetch(`${API_BASE}/devis-public/${token}/programme`)
      .then(r => { if (r.ok && r.headers.get('content-type')?.includes('pdf')) setProgrammeDisponible(true); })
      .catch(() => {});
  }, [token]);

  function telPortableValide(tel) {
    const cleaned = tel.replace(/[\s.\-()]/g, '');
    // France : 06 ou 07 + 8 chiffres
    // International mobile : +33 6/7, +32 4, +41 7, etc. — indicatif + 9 chiffres min
    return /^0[67]\d{8}$/.test(cleaned) || /^(\+|00)(\d{1,3})[67]\d{7,9}$/.test(cleaned);
  }

  async function handleValider() {
    if (!form.signataire_nom.trim()) return setValMsg('Veuillez saisir votre nom');
    if (!form.signataire_tel.trim()) return setValMsg('Veuillez saisir votre numéro de portable');
    if (!telPortableValide(form.signataire_tel)) return setValMsg('Numéro de portable invalide (ex : 06 12 34 56 78 ou +33 6 12 34 56 78)');
    setValidating(true); setValMsg('');
    try {
      const res = await fetch(`${API_BASE}/devis-public/${token}/valider`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      setValidated(true);
    } catch (e) { setValMsg(e.message); }
    finally { setValidating(false); }
  }

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', fontFamily: 'Arial, sans-serif' }}>
      <p style={{ color: '#666' }}>Chargement du devis...</p>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ background: '#fff', borderRadius: '12px', padding: '2.5rem', maxWidth: '400px', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
        <h2 style={{ color: '#E87722', marginBottom: '0.5rem' }}>Lien invalide</h2>
        <p style={{ color: '#666' }}>{error}</p>
      </div>
    </div>
  );

  const devis = data.devis;
  const dejaValide = devis.statut_devis === 'Validé';

  return (
    <div style={{ minHeight: '100vh', background: '#f0f0f0', fontFamily: 'Arial, sans-serif' }}>

      {/* Header CEJ */}
      <div style={{ background: '#E87722', padding: '1rem 2rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <div style={{ color: '#fff', fontWeight: 'bold', fontSize: '1.3rem' }}>CEJ Concept</div>
        <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>— Gestion Formation</div>
      </div>

      {/* Bandeau statut */}
      {(dejaValide || validated) && (
        <div style={{ background: '#16a34a', color: '#fff', padding: '0.8rem 2rem', textAlign: 'center', fontWeight: 600, fontSize: '1rem' }}>
          ✓ Ce devis a été validé
          {devis.signataire_nom && ` par ${devis.signataire_nom}`}
          {devis.date_signature && ` le ${new Date(devis.date_signature).toLocaleDateString('fr-FR')}`}
        </div>
      )}

      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '2rem 1rem', display: 'grid', gridTemplateColumns: '1fr 340px', gap: '1.5rem', alignItems: 'start' }}>

        {/* Document devis */}
        <div>
          <div style={{ background: '#fff', borderRadius: '8px', boxShadow: '0 2px 12px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
            {/* Barre outils */}
            <div style={{ padding: '0.8rem 1.2rem', background: '#f8f8f8', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <strong style={{ color: '#333' }}>{devis.devis_id}</strong>
                <span style={{ color: '#666', marginLeft: '0.5rem', fontSize: '0.9rem' }}>{devis.intitule_formation}</span>
              </div>
              <a href={`${API_BASE}/devis-public/${token}/pdf`} target="_blank" rel="noreferrer"
                style={{ padding: '0.4rem 1rem', background: '#E87722', color: '#fff', borderRadius: '6px', textDecoration: 'none', fontSize: '0.85rem', fontWeight: 600 }}>
                ⬇ Télécharger PDF
              </a>
            </div>
            {/* HTML du devis */}
            <div style={{ padding: '0', overflow: 'auto' }}
              dangerouslySetInnerHTML={{ __html: data.html }} />
          </div>
        </div>

        {/* Panneau de droite */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', position: 'sticky', top: '1rem' }}>

          {/* Validation */}
          {validated ? (
            <div style={{ background: '#fff', borderRadius: '12px', padding: '2rem', boxShadow: '0 2px 12px rgba(0,0,0,0.1)', textAlign: 'center' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
              <h3 style={{ color: '#16a34a', marginBottom: '0.5rem' }}>Devis validé !</h3>
              <p style={{ color: '#666', fontSize: '0.9rem', lineHeight: 1.6 }}>
                Votre validation a bien été enregistrée.<br />
                Veuillez maintenant compléter votre <strong>fiche d'inscription</strong> ci-dessous pour finaliser votre dossier.
              </p>
            </div>
          ) : dejaValide ? (
            <div style={{ background: '#fff', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 12px rgba(0,0,0,0.1)' }}>
              <h3 style={{ color: '#16a34a', marginBottom: '0.5rem' }}>✓ Devis validé</h3>
              <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.8rem' }}>
                Ce devis a été accepté. Veuillez compléter votre fiche d'inscription pour finaliser votre dossier.
              </p>
              {devis.signataire_nom && (
                <div style={{ background: '#f0fdf4', borderRadius: '6px', padding: '0.7rem' }}>
                  <div style={{ fontSize: '0.85rem', color: '#166534' }}>Signé par : <strong>{devis.signataire_nom}</strong></div>
                  <div style={{ fontSize: '0.85rem', color: '#166534' }}>Tél : {devis.signataire_tel}</div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ background: '#fff', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 12px rgba(0,0,0,0.1)' }}>
              <h3 style={{ color: '#333', marginBottom: '0.3rem', fontSize: '1.1rem' }}>Valider ce devis</h3>
              <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: '1.2rem', lineHeight: 1.5 }}>
                Pour accepter ce devis, saisissez vos informations et cliquez sur "Valider".
              </p>

              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#555', marginBottom: '0.25rem' }}>
                Nom et prénom *
              </label>
              <input type="text" value={form.signataire_nom}
                onChange={e => setForm(f => ({ ...f, signataire_nom: e.target.value }))}
                placeholder="Ex: Jean DUPONT"
                style={{ width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.9rem', marginBottom: '0.8rem', boxSizing: 'border-box' }} />

              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#555', marginBottom: '0.25rem' }}>
                Téléphone portable *
              </label>
              <input type="tel" value={form.signataire_tel}
                onChange={e => setForm(f => ({ ...f, signataire_tel: e.target.value }))}
                placeholder="Ex: 06 12 34 56 78"
                style={{ width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.9rem', marginBottom: '1rem', boxSizing: 'border-box' }} />

              {valMsg && (
                <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '6px', padding: '0.5rem 0.7rem', color: '#dc2626', fontSize: '0.85rem', marginBottom: '0.8rem' }}>
                  {valMsg}
                </div>
              )}

              <button onClick={handleValider} disabled={validating}
                style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', background: '#E87722', color: '#fff', border: 'none', fontWeight: 700, fontSize: '1rem', cursor: validating ? 'not-allowed' : 'pointer', opacity: validating ? 0.7 : 1 }}>
                {validating ? '⏳ Validation...' : '✓ Valider le devis'}
              </button>

              <p style={{ color: '#999', fontSize: '0.75rem', marginTop: '0.8rem', textAlign: 'center', lineHeight: 1.4 }}>
                En validant, vous acceptez ce devis au nom de votre organisation.<br />
                Votre validation sera horodatée et enregistrée.
              </p>
            </div>
          )}

          {/* Téléchargement */}
          <div style={{ background: '#fff', borderRadius: '12px', padding: '1.2rem', boxShadow: '0 2px 12px rgba(0,0,0,0.1)' }}>
            <h4 style={{ color: '#333', marginBottom: '0.8rem', fontSize: '0.95rem' }}>📄 Téléchargement</h4>
            <a href={`${API_BASE}/devis-public/${token}/pdf`} target="_blank" rel="noreferrer"
              style={{ display: 'block', textAlign: 'center', padding: '0.6rem', borderRadius: '6px', background: '#f5f5f5', border: '1px solid #ddd', color: '#333', textDecoration: 'none', fontSize: '0.85rem', fontWeight: 600 }}>
              ⬇ Télécharger le devis en PDF
            </a>
          </div>

          {/* Programme de formation */}
          {programmeDisponible && (
            <div style={{ background: '#fff', borderRadius: '12px', padding: '1.2rem', boxShadow: '0 2px 12px rgba(0,0,0,0.1)', border: '1px solid #fde8d0' }}>
              <h4 style={{ color: '#333', marginBottom: '0.5rem', fontSize: '0.95rem' }}>📚 Programme de formation</h4>
              <p style={{ color: '#666', fontSize: '0.82rem', marginBottom: '0.8rem', lineHeight: 1.4 }}>
                Le programme détaillé de la formation est disponible.
              </p>
              <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
                <a
                  href={`${API_BASE}/devis-public/${token}/programme`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: 'block', textAlign: 'center', padding: '0.55rem', borderRadius: '6px', background: '#fff7f0', border: '1px solid #E87722', color: '#E87722', textDecoration: 'none', fontSize: '0.85rem', fontWeight: 600 }}>
                  👁 Consulter le programme
                </a>
                <a
                  href={`${API_BASE}/devis-public/${token}/programme`}
                  download
                  style={{ display: 'block', textAlign: 'center', padding: '0.55rem', borderRadius: '6px', background: '#E87722', color: '#fff', textDecoration: 'none', fontSize: '0.85rem', fontWeight: 600 }}>
                  ⬇ Télécharger le programme
                </a>
              </div>
            </div>
          )}

          {/* Fiche d'inscription — accessible dès validation du devis */}
          {(dejaValide || validated) && (
            <div style={{ background: '#fff7f0', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 12px rgba(0,0,0,0.1)', border: '2px solid #E87722' }}>
              <h4 style={{ color: '#E87722', marginBottom: '0.5rem', fontSize: '1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                📋 Fiche d'inscription
              </h4>
              <p style={{ color: '#555', fontSize: '0.85rem', marginBottom: '1rem', lineHeight: 1.6 }}>
                Complétez votre fiche d'inscription pour finaliser votre dossier de formation. Cette étape est nécessaire avant le début de la formation.
              </p>
              <a
                href={`/fiche-inscription/${token}`}
                style={{ display: 'block', textAlign: 'center', padding: '0.85rem', borderRadius: '8px', background: '#E87722', color: '#fff', textDecoration: 'none', fontSize: '0.95rem', fontWeight: 700, letterSpacing: '0.01em', boxShadow: '0 2px 8px rgba(232,119,34,0.35)' }}>
                📋 Compléter la fiche d'inscription →
              </a>
            </div>
          )}

          {/* Contact */}
          <div style={{ background: '#fff', borderRadius: '12px', padding: '1.2rem', boxShadow: '0 2px 12px rgba(0,0,0,0.1)', fontSize: '0.85rem', color: '#666', lineHeight: 1.6 }}>
            <h4 style={{ color: '#333', marginBottom: '0.5rem', fontSize: '0.95rem' }}>📞 Contact</h4>
            <p>CEJ Concept</p>
            <p>formation@cejconcept.fr</p>
            <p>03.74.83.01.77</p>
          </div>

        </div>
      </div>

      {/* Popup fiche d'inscription — affiché au moment de la validation */}
      {showFichePopup && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '2.5rem 2rem', maxWidth: '420px', width: '100%', textAlign: 'center', boxShadow: '0 8px 40px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: '4rem', marginBottom: '0.75rem' }}>✅</div>
            <h2 style={{ color: '#16a34a', marginBottom: '0.75rem', fontSize: '1.4rem' }}>Devis validé !</h2>
            <p style={{ color: '#555', fontSize: '0.95rem', lineHeight: 1.6, marginBottom: '1.75rem' }}>
              Merci pour votre confirmation.<br />
              Pour finaliser votre inscription, veuillez compléter la <strong>fiche d'inscription</strong>.
            </p>
            <a href={`/fiche-inscription/${token}`}
              style={{ display: 'block', padding: '1rem', borderRadius: '10px', background: '#E87722', color: '#fff', textDecoration: 'none', fontSize: '1rem', fontWeight: 700, marginBottom: '1rem', boxShadow: '0 2px 10px rgba(232,119,34,0.4)' }}>
              📋 Compléter ma fiche d'inscription
            </a>
            <button onClick={() => setShowFichePopup(false)}
              style={{ background: 'none', border: 'none', color: '#999', fontSize: '0.85rem', cursor: 'pointer', textDecoration: 'underline' }}>
              Plus tard
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
